import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ArrowRight, Star, MapPin, Loader2, PawPrint, User } from "lucide-react";
import { marked } from "marked";
import { api } from "../lib/api";
import Breadcrumbs from "../components/Breadcrumbs";
import JsonLd from "../components/JsonLd";
import StaticGuideBlock from "../components/StaticGuideBlock";
import { getCategory } from "../lib/categories";
import { placeUrl } from "../lib/placeUrl";
import { getPost as getStaticGuide } from "../lib/blogPosts";

const SITE_URL = "https://pawground.app";

// `marked` config — tight & safe rendering of Mongo-backed markdown guides.
marked.setOptions({
  gfm: true,
  breaks: false,
  headerIds: false,
  mangle: false,
});

/**
 * /guides/:slug — unified guide detail page.
 *
 * Resolves content from two sources, in order:
 *   1. Static guide registry (lib/blogPosts.js) — block-schema content
 *      that lived at /blog/* before the Blog→Guides merge. Rendered
 *      synchronously (no API call) so SEO crawlers see the H1
 *      immediately.
 *   2. MongoDB-backed guides (/api/guides/:slug) — markdown body,
 *      hydrated related_places.
 *
 * Static content wins when a slug exists in both places (defensive: an
 * editor could accidentally seed the same slug into Mongo).
 */
const GuideDetail = () => {
  const { slug } = useParams();
  const staticGuide = getStaticGuide(slug);

  const [apiGuide, setApiGuide] = useState(null);
  const [loading, setLoading] = useState(!staticGuide);
  const [notFound, setNotFound] = useState(false);

  // Derived from the URL slug — used by the `!guide` render branch
  // below to show a substantive fallback (title-cased slug) when the
  // API fetch is blocked or fails. Mirrors ParkDetail.jsx's
  // `seededFromSlug` pattern (9/2026 fix): Googlebot cannot fetch
  // `/api/*` per robots.txt, so an unpatched `if (!guide) return null`
  // erases the prerender-baked HTML body and leaves the crawler with
  // an empty page. Rendering a slug-derived title-case fallback keeps
  // substantive H1 text on screen regardless of fetch state, so the
  // prerender-baked `<meta name="robots" content="index, follow">`
  // stays paired with a page that has actual visible content.
  //
  // Guide slugs (unlike place slugs) don't carry the `-[a-z0-9]{6}`
  // id-hash suffix — they're human-authored (e.g.
  // `best-dog-parks-puerto-rico`). So we just title-case the dashes
  // straight through with no suffix stripping.
  const slugFallbackTitle = useMemo(() => {
    if (!slug) return "";
    return slug
      .split("-")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }, [slug]);

  // Only call the API when no static guide matched.
  useEffect(() => {
    if (staticGuide) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/guides/${slug}`)
      .then((r) => {
        if (cancelled) return;
        setApiGuide(r.data);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, staticGuide]);

  // Build a normalized "view-model" so the JSX below stays one shape,
  // regardless of which source supplied the content.
  const guide = staticGuide
    ? {
        slug: staticGuide.slug,
        title: staticGuide.title,
        meta_description: staticGuide.excerpt,
        author: staticGuide.author
          ? `${staticGuide.author} — ${staticGuide.authorTitle || "Pawground"}`
          : "Cesar from Pawground",
        published_date: staticGuide.publishedAt,
        updated_date: staticGuide.updatedAt || staticGuide.publishedAt,
        featured_image: staticGuide.coverImage,
        city: null,
        state: null,
        kind: "static",
        body_blocks: staticGuide.body || [],
        body_markdown: null,
        related_places: [],
        excerpt: staticGuide.excerpt,
        readMinutes: staticGuide.readMinutes,
        coverAlt: staticGuide.coverAlt,
      }
    : apiGuide
    ? { ...apiGuide, kind: "api", body_markdown: apiGuide.body, body_blocks: null }
    : null;

  useEffect(() => {
    if (!guide) return;
    document.title = `${guide.title} | Pawground`;
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        if (selector.startsWith("meta[")) {
          el = document.createElement("meta");
          const propMatch = selector.match(/property="([^"]+)"/);
          const nameMatch = selector.match(/name="([^"]+)"/);
          if (propMatch) el.setAttribute("property", propMatch[1]);
          else if (nameMatch) el.setAttribute("name", nameMatch[1]);
        } else if (selector.startsWith("link[")) {
          el = document.createElement("link");
          const relMatch = selector.match(/rel="([^"]+)"/);
          if (relMatch) el.setAttribute("rel", relMatch[1]);
        } else {
          return;
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    const canonical = `${SITE_URL}/guides/${guide.slug}`;
    setMeta('meta[name="description"]', "content", guide.meta_description || "");
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta('meta[property="og:title"]', "content", `${guide.title} | Pawground`);
    setMeta('meta[property="og:description"]', "content", guide.meta_description || "");
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[name="twitter:title"]', "content", `${guide.title} | Pawground`);
    setMeta('meta[name="twitter:description"]', "content", guide.meta_description || "");
  }, [guide]);

  if (notFound) return <Navigate to="/guides" replace />;
  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16" data-testid="guide-detail-loading">
        <div className="flex items-center gap-2 text-brand-ink/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading guide…
        </div>
      </div>
    );
  }
  // Slug-derived fallback: fetch succeeded but returned no doc, OR
  // fetch failed with a non-404 (e.g. blocked by robots.txt for
  // Googlebot, network abort, 5xx). We MUST NOT return null here —
  // that erases the prerender-baked HTML shell that Googlebot has
  // already indexed. Instead render a title-case rendition of the
  // slug so visible content stays paired with the prerender-baked
  // `<meta name="robots" content="index, follow">`. Same pattern as
  // ParkDetail.jsx's `!park` branch (9/2026 fix).
  if (!guide) {
    return (
      <article
        className="bg-brand-cream min-h-[calc(100vh-4rem)]"
        data-testid="guide-detail-load-failed"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-20">
          <Breadcrumbs
            items={[
              { name: "Guides", to: "/guides" },
              { name: slugFallbackTitle || "Guide" },
            ]}
          />
          <Link
            to="/guides"
            data-testid="guide-back-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-forest hover:text-brand-forest-dark transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All guides
          </Link>
          <header className="mt-6">
            <h1
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-brand-ink leading-[1.08] tracking-tight"
              data-testid="guide-title"
            >
              {slugFallbackTitle || "Guide"}
            </h1>
            <p
              className="text-lg text-brand-ink/70 mt-4 leading-relaxed"
              data-testid="guide-fallback-tagline"
            >
              Honest, locally-written guides from Pawground&apos;s community of
              dog parents.
            </p>
          </header>
          <p
            className="text-brand-ink/50 text-sm mt-8"
            data-testid="guide-detail-load-failed-note"
          >
            Live details are unavailable right now — try again in a moment.
          </p>
        </div>
      </article>
    );
  }

  const url = `${SITE_URL}/guides/${guide.slug}`;
  const imageUrl = guide.featured_image
    ? guide.featured_image.startsWith("http")
      ? guide.featured_image
      : `${SITE_URL}${guide.featured_image}`
    : `${SITE_URL}/icon-512.png`;

  // Article schema is the same shape for both sources — guarantees Google
  // sees consistent rich-snippet data regardless of where the content
  // came from.
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    description: guide.meta_description || guide.excerpt,
    image: imageUrl,
    datePublished: guide.published_date,
    dateModified: guide.updated_date || guide.published_date,
    author: { "@type": "Person", name: guide.author || "Cesar from Pawground" },
    publisher: {
      "@type": "Organization",
      name: "Pawground",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/icon-512.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
  };

  // Render markdown → HTML for Mongo-backed guides. Static guides use
  // the block-schema renderer below — different code path.
  const bodyHtml = guide.kind === "api" ? marked.parse(guide.body_markdown || "") : null;

  return (
    <article className="bg-brand-cream min-h-[calc(100vh-4rem)]" data-testid="guide-detail-page">
      <JsonLd schema={articleSchema} id="guide-article-schema" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-20">
        <Breadcrumbs
          items={[
            { name: "Guides", to: "/guides" },
            { name: guide.title },
          ]}
        />
        <Link
          to="/guides"
          data-testid="guide-back-link"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-forest hover:text-brand-forest-dark transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> All guides
        </Link>

        <header className="mt-6">
          <div className="flex flex-wrap items-center gap-3 text-xs text-brand-ink/60 mb-3">
            <span className="inline-flex items-center gap-1.5" data-testid="guide-pub-date">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(guide.published_date).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </span>
            <span className="text-brand-ink/60" data-testid="guide-author">
              · by {guide.author}
            </span>
            {guide.readMinutes && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> {guide.readMinutes} min read
              </span>
            )}
            {guide.city && guide.state && (
              <span className="bg-brand-light-green text-brand-forest text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">
                {guide.city}, {guide.state}
              </span>
            )}
          </div>
          <h1
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-brand-ink leading-[1.08] tracking-tight"
            data-testid="guide-title"
          >
            {guide.title}
          </h1>
          {guide.meta_description && (
            <p className="text-lg text-brand-ink/70 mt-4 leading-relaxed">
              {guide.meta_description}
            </p>
          )}
        </header>

        {guide.featured_image && (
          <figure className="mt-8">
            <img
              src={guide.featured_image}
              alt={guide.coverAlt || guide.title}
              loading="lazy"
              className="w-full aspect-[4/3] sm:aspect-[16/10] object-cover object-top rounded-3xl border border-brand-border-warm shadow-warm-lg"
              data-testid="guide-cover"
            />
          </figure>
        )}

        {/* Body — static-block renderer for the legacy /blog content;
            markdown renderer for new Mongo-backed guides. */}
        {guide.kind === "static" ? (
          <div className="mt-8" data-testid="guide-body">
            {guide.body_blocks.map((block, i) => (
              <StaticGuideBlock key={i} block={block} />
            ))}
          </div>
        ) : (
          <div
            className="guide-body mt-8 max-w-none text-brand-ink/85 leading-relaxed"
            data-testid="guide-body"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        )}

        {/* Related places — only Mongo-backed guides currently carry these. */}
        {guide.related_places?.length > 0 && (
          <section className="mt-14" data-testid="guide-related-places">
            <h2 className="font-heading text-2xl font-bold text-brand-ink mb-5">
              Places mentioned in this guide
            </h2>
            <div className="space-y-3">
              {guide.related_places.map((p) => {
                const cat = getCategory(p.place_type || "park");
                return (
                  <Link
                    key={p.id}
                    to={placeUrl(p)}
                    data-testid={`guide-related-${p.id}`}
                    className="flex items-center justify-between gap-4 bg-white rounded-3xl p-5 border border-brand-border-warm hover:border-brand-forest hover:shadow-warm transition-all group"
                  >
                    <div className="min-w-0">
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded-full mb-1.5"
                        style={{ backgroundColor: cat.color }}
                      >
                        {cat.label}
                      </span>
                      <div className="font-heading text-lg font-bold text-brand-ink group-hover:text-brand-forest transition-colors truncate">
                        {p.name}
                      </div>
                      {p.address && (
                        <div className="flex items-center gap-1 text-xs text-brand-ink/60 mt-1">
                          <MapPin className="w-3 h-3" /> <span className="truncate">{p.address}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {p.rating && (
                        <div className="flex items-center gap-1 text-sm">
                          <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                          <span className="font-semibold text-brand-ink">{p.rating.toFixed(1)}</span>
                        </div>
                      )}
                      <ArrowRight className="w-4 h-4 text-brand-forest" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Conversion CTA — matches the previous blog post's final CTA. */}
        <aside
          data-testid="guide-join-cta"
          className="mt-14 rounded-3xl p-8 sm:p-10 bg-brand-forest text-white shadow-warm-lg text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-15 pointer-events-none" aria-hidden>
            <PawPrint className="absolute -top-4 -left-4 w-32 h-32 rotate-[-12deg]" strokeWidth={1.5} />
            <PawPrint className="absolute -bottom-6 -right-6 w-40 h-40 rotate-[14deg]" strokeWidth={1.5} />
          </div>
          <div className="relative">
            <span className="inline-flex items-center gap-2 bg-white/15 text-white text-[11px] font-semibold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full">
              <PawPrint className="w-3.5 h-3.5" />
              Free · No credit card
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-black mt-4 leading-tight">
              Join Pawground — built for real dog parents
            </h2>
            <p className="text-white/85 mt-3 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              Save your favorite parks, check in when you arrive, and see which spots are busy right now — straight from local pet parents.
            </p>
            <Link
              to="/signup"
              data-testid="guide-signup-cta"
              className="inline-flex items-center gap-2 mt-6 bg-white text-brand-forest font-bold rounded-full px-7 py-3 text-base hover:bg-brand-cream transition-colors active:scale-95 shadow-warm-lg"
            >
              <User className="w-4 h-4" />
              Create your free account
            </Link>
          </div>
        </aside>
      </div>
    </article>
  );
};

export default GuideDetail;
